<template>
  <b-card>
    <b-row>
      <!-- User Info: Left col -->
      <b-col
          class="d-flex justify-content-between flex-column"
          cols="12"
          xl="6"
      >
        <!-- Bank Avatar & Action Buttons -->
        <div class="d-flex justify-content-start">
          <b-avatar
              :src="bankData.avatar"
              :text="avatarText(bankData.name)"
              :variant="`light-${resolveBankStatusVariant(bankData.name)}`"
              class="flex-shrink-1"
              rounded
              size="100px"
          />
          <div class="w-100 d-flex flex-column ml-1">
            <div class="mb-1">
              <h4 class="mb-0">
                {{ bankData.name }}
              </h4>
              <span class="card-text"
              >{{
                  amountToCurrency(bankData.amount, "fr-FR", null)
                }}
                XOF</span
              >
            </div>
            <div class="d-flex justify-content-between">
              <b-button variant="success" @click="openModal(CREDIT_MODAL)">
                Entrée
              </b-button>
              <b-button
                  variant="danger"
                  @click="openModal(DEBIT_MODAL)"
              >
                Sortie
              </b-button>
              <b-button
                  variant="primary"
                  @click="openModal(TRANSFER_MODAL)"
              >
                Transfert
              </b-button>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Right Col: Table -->
      <b-col cols="12" xl="6">
        <table class="mt-2 mt-xl-0 w-100">
          <tr>
            <th class="pb-50">
              <feather-icon class="mr-75" icon="UserIcon"/>
              <span class="font-weight-bold">Responsable</span>
            </th>
            <td class="pb-50">
              {{ bankData.manager ? bankData.manager.name : "----------" }}
            </td>
          </tr>
          <tr>
            <th class="pb-50">
              <feather-icon class="mr-75" icon="StarIcon"/>
              <span class="font-weight-bold">Crée le :</span>
            </th>
            <td class="pb-50 text-capitalize">
              {{ formatDate(bankData.created_at) }}
            </td>
          </tr>
          <tr>
            <th class="pb-50">
              <feather-icon class="mr-75" icon="FlagIcon"/>
              <span class="font-weight-bold">Dernière modification :</span>
            </th>
            <td class="pb-50">
              {{ formatDate(bankData.updated_at) }}
            </td>
          </tr>
        </table>
      </b-col>
    </b-row>
    <b-row>
      <!-- Bank Info: Right col -->
      <b-col
          class="d-flex justify-content-between flex-column"
          cols="12"
          offset-xl="6"
          xl="6"
      >
        <!-- Bank Stats -->
        <div class="d-flex align-items-center justify-content-between mt-2">
          <div class="d-flex align-items-center">
            <b-avatar rounded variant="light-info">
              <feather-icon icon="DollarSignIcon" size="18"/>
            </b-avatar>
            <div class="ml-1">
              <h6 class="mb-0">
                {{ amountToCurrency(bankData.annual_in_flow, "fr-FR", null) }}
                XOF
              </h6>
              <small>Total Annuel Sortie</small>
            </div>
          </div>

          <div class="d-flex align-items-center">
            <b-avatar rounded variant="light-danger">
              <feather-icon icon="DollarSignIcon" size="18"/>
            </b-avatar>
            <div class="ml-1">
              <h6 class="mb-0">
                {{ amountToCurrency(bankData.annual_out_flow, "fr-FR", null) }}
                XOF
              </h6>
              <small>Total Annuel Sortie</small>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <bank-operation-modal
        :current-bank-data="bankData"
        :is-loading="isLoading"
        :is-opened.sync="isOpened"
        :modal-id="'bank-operation-modal'"
        :modal-type="modalType"
        @submit="handleOperation"
    />
  </b-card>
</template>

<script>
import {BAvatar, BButton, BCard, BCol, BRow} from "bootstrap-vue";
import {avatarText, formatDate} from "@core/utils/filter";
import useBanksList from "@/views/apps/bank/banks-list/useBanksList";
import {amountToCurrency} from "@/utils/formatAmount";
import BankOperationModal from "@/views/apps/bank/banks-view/BankOperationModal.vue";
import {ref} from "@vue/composition-api";
import store from "@/store";
import {useToast} from "vue-toastification/composition";
import ToastificationContent from "@/@core/components/toastification/ToastificationContent.vue";
import {
  CREDIT_MODAL,
  CREDIT_OPERATION,
  CREDIT_TYPE,
  DEBIT_MODAL,
  DEBIT_TYPE,
  EXPENSE_OPERATION,
  TRANSFER_MODAL,
  TRANSFER_OPERATION
} from "@/constants/finance";

export default {
  components: {
    BankOperationModal,
    BCard,
    BRow,
    BCol,
    BAvatar,
    BButton,
  },
  props: {
    bankData: {
      type: Object,
      required: true,
    },
  },
  setup(props, {emit}) {
    const {resolveBankStatusVariant} = useBanksList();
    const toast = useToast();
    const modalType = ref(CREDIT_MODAL);
    const isOpened = ref(false);
    const isLoading = ref(false);

    const openModal = (type) => {
      modalType.value = type;
      isOpened.value = true;
    };

    const handleOperation = async (data) => {
      try {
        const {amount, expense_type, concerned_project, destination_account, designation} = data;

        const defaultData = {
          sub_cash_register: props.bankData.uid,
          amount,
          designation: designation || `Opération sur le compte`,
        };

        // Handle operation
        if (modalType.value === CREDIT_MODAL) {
          data = {
            ...defaultData,
            transaction: CREDIT_TYPE,
            type: CREDIT_OPERATION
          };
        } else {
          data = {
            ...defaultData,
            transaction: DEBIT_TYPE,
            ...(modalType.value === DEBIT_MODAL ? {
                  type: EXPENSE_OPERATION,
                  expense: {
                    type: expense_type,
                    project: concerned_project,
                  },
                } : {
                  type: TRANSFER_OPERATION,
                  from_cash_register: false,
                  destination_account
                }
            ),
          };
        }
        await store.dispatch("fundHistory/addFundHistory", data);

        showToast(
            modalType.value === CREDIT_MODAL
                ? "Entrée enregistrée avec succès !"
                : (modalType.value === DEBIT_MODAL ? "Sortie enregistrée" : "Transfert enregistré") + " avec succès !",
            "CheckCircleIcon",
            "success"
        );

        isOpened.value = false;
        emit("operation-done");
      } catch (error) {
        console.log("Erreur lors de l'enregistrement de l'opération !");
      } finally {
        isLoading.value = false;
      }
    };

    const showToast = (message, icon, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: icon,
          variant: variant,
        },
      });
    };

    return {
      CREDIT_MODAL,
      DEBIT_MODAL,
      TRANSFER_MODAL,
      avatarText,
      formatDate, amountToCurrency,
      resolveBankStatusVariant,
      modalType,
      isLoading,
      isOpened,
      openModal,
      handleOperation,
    };
  },
};
</script>
